import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, CaretDown, Phone } from "@phosphor-icons/react";
import Wordmark from "@/components/Wordmark";
import { CONTACT } from "@/data/content";

const SERVICE_LINKS = [
  { to: "/hvac-service", label: "HVAC Service & Diagnostics" },
  { to: "/furnace-installation", label: "Furnace Installation" },
  { to: "/ac-installation", label: "AC Installation" },
  { to: "/light-commercial-hvac", label: "Light Commercial HVAC" },
  { to: "/rtu-change-outs", label: "RTU Change-Outs" },
  { to: "/preventive-maintenance", label: "Preventive Maintenance" },
  { to: "/technology-diagnostics", label: "Technology & Diagnostics" },
];

const NAV = [
  { to: "/projects", label: "Projects" },
  { to: "/about", label: "About" },
  { to: "/property-managers", label: "Property Managers" },
  { to: "/contractor-partners", label: "Contractor Partners" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${scrolled ? "bg-ink/85 backdrop-blur-md border-b border-white/10" : "bg-transparent border-b border-transparent"}`}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 md:px-12">
        <Wordmark />

        <nav className="hidden items-center gap-8 lg:flex">
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button data-testid="nav-services" className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-slatex-300 transition-colors hover:text-white">
              Services <CaretDown size={12} weight="bold" />
            </button>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-1/2 top-full w-72 -translate-x-1/2 pt-4"
                >
                  <div className="border border-white/10 bg-panel panel-glow">
                    {SERVICE_LINKS.map((s) => (
                      <Link key={s.to} to={s.to} data-testid={`nav-service-${s.to.slice(1)}`}
                        className="block border-b border-white/5 px-5 py-3 font-body text-sm text-slatex-300 transition-colors last:border-0 hover:bg-panel2 hover:text-brand-cyan">
                        {s.label}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
              className="font-mono text-xs uppercase tracking-widest text-slatex-300 transition-colors hover:text-white">
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a href={`tel:${CONTACT.phoneRaw}`} data-testid="nav-phone" className="flex items-center gap-2 font-mono text-xs text-slatex-300 transition-colors hover:text-white">
            <Phone size={14} weight="fill" /> {CONTACT.phone}
          </a>
          <Link to="/contact" data-testid="nav-request-service"
            className="border border-brand-cyan/40 bg-brand-cyan/10 px-5 py-2.5 font-mono text-xs uppercase tracking-widest text-brand-cyan transition-colors hover:bg-brand-cyan hover:text-ink">
            Request Service
          </Link>
        </div>

        <button data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)} className="text-white lg:hidden">
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-ink lg:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              <span className="py-2 font-mono text-[10px] uppercase tracking-widest text-slatex-500">Services</span>
              {SERVICE_LINKS.map((s) => (
                <Link key={s.to} to={s.to} className="border-b border-white/5 py-3 font-body text-sm text-slatex-300">{s.label}</Link>
              ))}
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className="border-b border-white/5 py-3 font-body text-sm text-slatex-300">{n.label}</Link>
              ))}
              <Link to="/contact" data-testid="mobile-request-service" className="mt-4 border border-brand-cyan/40 bg-brand-cyan/10 py-3 text-center font-mono text-xs uppercase tracking-widest text-brand-cyan">
                Request Service
              </Link>
              <a href={`tel:${CONTACT.phoneRaw}`} className="py-3 text-center font-mono text-xs text-slatex-300">{CONTACT.phone}</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
